// src/data.ts
var popovers = /* @__PURE__ */ new Set();
var invokers = /* @__PURE__ */ new Set();
var popoverInvokerSupportedElements = [
  "button",
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="image"]',
  'input[type="reset"]'
];
var popoverInvokerAttributes = [
  "popoverToggleTarget",
  "popoverShowTarget",
  "popoverHideTarget"
];
var popoverInvokerSelector = popoverInvokerSupportedElements.flatMap((s) => {
  return popoverInvokerAttributes.map((a) => `${s}[${a}]`);
}).join(", ");

// src/popover-helpers.ts
var initialAriaExpandedValue = /* @__PURE__ */ new WeakMap();
function* getInvokersFor(el) {
  if (!popovers.has(el))
    return;
  for (const invoker of invokers) {
    if (getPopoverFor(invoker) === el)
      yield invoker;
  }
}
function getPopoverFor(el) {
  return el.popoverToggleTargetElement || el.popoverShowTargetElement || el.popoverHideTargetElement;
}
function setInvokerAriaExpanded(el) {
  if (!initialAriaExpandedValue.has(el)) {
    initialAriaExpandedValue.set(el, el.getAttribute("aria-expanded"));
  }
  const popover = getPopoverFor(el);
  if (popover) {
    invokers.add(el);
  } else {
    invokers.delete(el);
  }
  if (popover && popover.popover === "auto") {
    el.setAttribute(
      "aria-expanded",
      String(popover.classList.contains(":open"))
    );
  } else {
    const initialValue = initialAriaExpandedValue.get(el);
    if (!initialValue) {
      el.removeAttribute("aria-expanded");
    } else {
      el.setAttribute("aria-expanded", initialValue);
    }
  }
}
function* getPopoversFromNode(node) {
  if (node instanceof HTMLElement && node.hasAttribute("popover")) {
    yield node;
  }
  if (node instanceof Document || node instanceof ShadowRoot || node instanceof HTMLElement) {
    for (const el of node.querySelectorAll("[popover]")) {
      if (el instanceof HTMLElement) {
        yield el;
      }
    }
  }
}
function* getInvokersFromNode(node) {
  if ((node instanceof HTMLInputElement || node instanceof HTMLButtonElement) && node.matches(popoverInvokerSelector)) {
    yield node;
  }
  if (node instanceof Document || node instanceof ShadowRoot || node instanceof HTMLElement) {
    for (const el of node.querySelectorAll(popoverInvokerSelector)) {
      if (el instanceof HTMLInputElement || el instanceof HTMLButtonElement) {
        yield el;
      }
    }
  }
}

// src/observer.ts
var handleChildListMutation = (mutation) => {
  for (const node of mutation.addedNodes) {
    for (const invoker of getInvokersFromNode(node)) {
      setInvokerAriaExpanded(invoker);
    }
    for (const popover of getPopoversFromNode(node)) {
      popovers.add(popover);
    }
  }
  for (const node of mutation.removedNodes) {
    for (const invoker of getInvokersFromNode(node)) {
      invokers.delete(invoker);
    }
    for (const popover of getPopoversFromNode(node)) {
      popovers.delete(popover);
    }
  }
};
var handlePopoverAttributeMutation = (mutation) => {
  const target = mutation.target;
  if (target instanceof HTMLElement) {
    if (target.hasAttribute("popover")) {
      popovers.add(target);
    } else {
      popovers.delete(target);
    }
  }
};
var handleInvokerAttributeMutation = (mutation) => {
  const target = mutation.target;
  if (target instanceof HTMLButtonElement || target instanceof HTMLInputElement) {
    setInvokerAriaExpanded(target);
  }
};
var handleMutation = (mutationList) => {
  mutationList.forEach((mutation) => {
    switch (mutation.type) {
      case "attributes":
        switch (mutation.attributeName) {
          case "popover":
            handlePopoverAttributeMutation(mutation);
            break;
          default:
            handleInvokerAttributeMutation(mutation);
            break;
        }
        break;
      case "childList":
        handleChildListMutation(mutation);
        break;
    }
  });
};
var observer = new MutationObserver(handleMutation);
var observePopoversMutations = (root) => {
  if (root === document) {
    for (const popover of getPopoversFromNode(root)) {
      popovers.add(popover);
    }
    for (const invoker of getInvokersFromNode(root)) {
      setInvokerAriaExpanded(invoker);
    }
  }
  observer.observe(root, {
    attributeFilter: ["popover", ...popoverInvokerAttributes],
    childList: true,
    subtree: true
  });
};

// src/popover.ts
function isSupported() {
  return typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype === "object" && "popover" in HTMLElement.prototype;
}
function patchAttachShadow(callback) {
  const originalAttachShadow = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function(init) {
    const shadowRoot = originalAttachShadow.call(this, init);
    callback(shadowRoot);
    return shadowRoot;
  };
}
var closestElement = (selector, target) => {
  const found = target.closest(selector);
  if (found) {
    return found;
  }
  const root = target.getRootNode();
  if (root === document || !(root instanceof ShadowRoot)) {
    return null;
  }
  return closestElement(selector, root.host);
};
var queryAncestorAll = (element, selector, popovers2 = []) => {
  const ancestor = closestElement(selector, element);
  const parent = ancestor?.parentElement || ancestor?.getRootNode()?.host;
  return ancestor && parent ? queryAncestorAll(parent, selector, [ancestor, ...popovers2]) : popovers2;
};
function apply() {
  const visibleElements = /* @__PURE__ */ new WeakSet();
  function checkPopoverValidity(element, expectedToBeShowing) {
    if (element.popover !== "auto" && element.popover !== "manual") {
      return false;
    }
    if (!element.isConnected)
      return false;
    if (element instanceof HTMLDialogElement && element.hasAttribute("open")) {
      return false;
    }
    if (expectedToBeShowing && !visibleElements.has(element))
      return false;
    if (!expectedToBeShowing && visibleElements.has(element))
      return false;
    if (document.fullscreenElement === element)
      return false;
    return true;
  }
  function assertPopoverValidity(element, expectedToBeShowing) {
    if (!checkPopoverValidity(element, expectedToBeShowing)) {
      throw new DOMException(
        "Cannot show or hide popover on invalid or already visible element",
        "InvalidStateError"
      );
    }
  }
  class BeforeToggleEvent extends Event {
    constructor(type, {
      currentState = "",
      newState = "",
      ...init
    } = {}) {
      super(type, init);
      this.currentState = String(currentState || "");
      this.newState = String(newState || "");
    }
  }
  window.BeforeToggleEvent = window.BeforeToggleEvent || BeforeToggleEvent;
  Object.defineProperties(HTMLElement.prototype, {
    popover: {
      enumerable: true,
      configurable: true,
      get() {
        const value = (this.getAttribute("popover") || "").toLowerCase();
        if (value === "manual")
          return "manual";
        if (value === "" || value == "auto")
          return "auto";
        return null;
      },
      set(value) {
        this.setAttribute("popover", value);
      }
    },
    showPopover: {
      enumerable: true,
      configurable: true,
      value() {
        assertPopoverValidity(this, false);
        const event = new BeforeToggleEvent("beforetoggle", {
          cancelable: true,
          currentState: "closed",
          newState: "open"
        });
        if (!this.dispatchEvent(event))
          return;
        assertPopoverValidity(this, false);
        this.classList.add(":open");
        visibleElements.add(this);
        if (this.popover === "auto") {
          const focusEl = this.hasAttribute("autofocus") ? this : this.querySelector("[autofocus]");
          focusEl?.focus();
          for (const invoker of getInvokersFor(this)) {
            setInvokerAriaExpanded(invoker);
          }
        }
      }
    },
    hidePopover: {
      enumerable: true,
      configurable: true,
      value() {
        assertPopoverValidity(this, true);
        this.dispatchEvent(
          new BeforeToggleEvent("beforetoggle", {
            cancelable: false,
            currentState: "open",
            newState: "closed"
          })
        );
        assertPopoverValidity(this, true);
        this.classList.remove(":open");
        visibleElements.delete(this);
        if (this.popover === "auto") {
          for (const invoker of getInvokersFor(this)) {
            setInvokerAriaExpanded(invoker);
          }
        }
      }
    }
  });
  const definePopoverTargetElementProperty = (name) => {
    const invokersMap = /* @__PURE__ */ new WeakMap();
    const invokerDescriptor = {
      set(targetElement) {
        if (targetElement === null) {
          this.removeAttribute(name.toLowerCase());
          invokersMap.delete(this);
        } else if (!(targetElement instanceof Element)) {
          throw new TypeError(`${name}Element must be an element or null`);
        } else {
          this.setAttribute(name.toLowerCase(), "");
          invokersMap.set(this, targetElement);
        }
      },
      get() {
        if (this.localName !== "button" && this.localName !== "input") {
          return null;
        }
        if (this.localName === "input" && this.type !== "reset" && this.type !== "image" && this.type !== "button") {
          return null;
        }
        if (this.disabled) {
          return null;
        }
        if (this.form && this.type === "submit") {
          return null;
        }
        const targetElement = invokersMap.get(this);
        if (!targetElement?.isConnected) {
          invokersMap.delete(this);
        }
        if (targetElement) {
          return targetElement;
        }
        const root = this.getRootNode();
        const idref = this.getAttribute(name.toLowerCase());
        if ((root instanceof Document || root instanceof ShadowRoot) && idref) {
          return root.getElementById(idref) || null;
        }
        return null;
      }
    };
    Object.defineProperty(
      HTMLButtonElement.prototype,
      `${name}Element`,
      invokerDescriptor
    );
    Object.defineProperty(
      HTMLInputElement.prototype,
      `${name}Element`,
      invokerDescriptor
    );
  };
  definePopoverTargetElementProperty("popoverToggleTarget");
  definePopoverTargetElementProperty("popoverShowTarget");
  definePopoverTargetElementProperty("popoverHideTarget");
  const handlePopoverTargetElementInvocation = (invoker) => {
    if (!(invoker instanceof HTMLButtonElement) && !(invoker instanceof HTMLInputElement)) {
      return;
    }
    let popoverTargetElement = null;
    if (invoker.popoverToggleTargetElement) {
      popoverTargetElement = invoker.popoverToggleTargetElement;
      if (popoverTargetElement) {
        if (visibleElements.has(popoverTargetElement)) {
          popoverTargetElement.hidePopover();
        } else {
          popoverTargetElement.showPopover();
        }
      }
    } else if (invoker.popoverShowTargetElement) {
      popoverTargetElement = invoker.popoverShowTargetElement;
      popoverTargetElement?.showPopover();
    } else if (invoker.popoverHideTargetElement) {
      popoverTargetElement = invoker.popoverHideTargetElement;
      popoverTargetElement?.hidePopover();
    }
    return popoverTargetElement;
  };
  const onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target?.shadowRoot) {
      return;
    }
    const root = target.getRootNode();
    if (!(root instanceof ShadowRoot || root instanceof Document)) {
      return;
    }
    const invoker = target.closest(popoverInvokerSelector);
    const popoverTargetElement = handlePopoverTargetElementInvocation(invoker);
    for (const popover of [...popovers]) {
      if (popover.matches('[popover="" i].\\:open, [popover=auto i].\\:open') && popover !== popoverTargetElement && !queryAncestorAll(target, "[popover]").includes(popover)) {
        popover.hidePopover();
      }
    }
  };
  const addOnClickEventListener = ((callback) => (root) => {
    root.addEventListener("click", callback);
  })(onClick);
  observePopoversMutations(document);
  addOnClickEventListener(document);
  patchAttachShadow(observePopoversMutations);
  patchAttachShadow(addOnClickEventListener);
}

// src/index.ts
if (!isSupported())
  apply();
//# sourceMappingURL=popover.js.map
